import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  useTransitionStatus,
} from '@floating-ui/react';
import cn from 'classnames';
import React, { forwardRef, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { InfoSmallIcon } from '../../foundations';

import { TOOLTIP_CLOSE_DELAY, TOOLTIP_OPEN_DELAY } from './tooltip-config';

import type { ForwardedRef, ReactNode } from 'react';

import type { TooltipProps } from './tooltip.types';

import './tooltip.scss';

/**
 * The tooltip component is supposed to display informative text when users hover over or tap an element
 *
 * ### Usage
 *
 * ```tsx
 * import { Tooltip } from '@bloomreach/react-banana-ui';
 *
 * export default function MyElementWithTooltip() {
 *   return (
 *     <Tooltip title="My tooltip">
 *       <span>Hover over me</span>
 *     </Tooltip>
 *   );
 * }
 * ```
 */
const Tooltip = forwardRef((props: TooltipProps, forwardedRef: ForwardedRef<HTMLDivElement>): ReactNode => {
  const {
    children,
    className,
    disablePortal = false,
    placement = 'top',
    portalRoot,
    renderOnIcon = false,
    title,
    ...rest
  } = props;

  const arrowRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const { context, floatingStyles, refs } = useFloating({
    middleware: [offset(12), flip(), shift(), arrow({ element: arrowRef })],
    onOpenChange: setVisible,
    open: visible,
    placement,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { delay: { close: TOOLTIP_CLOSE_DELAY, open: TOOLTIP_OPEN_DELAY } });
  const focus = useFocus(context);
  const role = useRole(context, { role: 'tooltip' });
  const { isMounted, status } = useTransitionStatus(context);

  const { getFloatingProps, getReferenceProps } = useInteractions([role, hover, focus]);

  const iconRef = useMemo(() => mergeRefs([refs.setReference]), [refs.setReference]);
  const childRef = useMemo(() => mergeRefs([refs.setReference, (children as any).ref]), [refs.setReference, children]);
  const containerRef = useMemo(() => mergeRefs([refs.setFloating, forwardedRef]), [refs.setFloating, forwardedRef]);

  if (!title) {
    return children;
  }

  const tooltipContent = (
    <div
      {...rest}
      {...getFloatingProps()}
      className={cn('rbui-tooltip', className)}
      data-placement={context.placement}
      ref={containerRef}
      style={{ ...floatingStyles }}
    >
      {isMounted && (
        <div className={cn('rbui-tooltip__inner', `rbui-tooltip__inner--${status}`)}>
          {title}
          <FloatingArrow
            className="rbui-tooltip__arrow"
            context={context}
            height={8}
            ref={arrowRef}
            tipRadius={2}
            width={18}
          />
        </div>
      )}
    </div>
  );

  const portalId = typeof portalRoot === 'string' ? portalRoot : undefined;
  const portalRef = typeof portalRoot !== 'string' ? portalRoot : undefined;

  const wrappedTooltip = disablePortal ? (
    tooltipContent
  ) : (
    <FloatingPortal id={portalId} root={portalRef}>
      {tooltipContent}
    </FloatingPortal>
  );

  // If renderOnIcon is enabled, wrap children with icon
  if (renderOnIcon) {
    const iconElement = renderOnIcon === true ? <InfoSmallIcon size={16} /> : renderOnIcon;

    return (
      <>
        <span className="rbui-tooltip__wrapper">
          {children}
          <span {...getReferenceProps({ className: 'rbui-tooltip__icon', ref: iconRef })}>{iconElement}</span>
        </span>
        {visible && wrappedTooltip}
      </>
    );
  }

  // Default behavior: attach tooltip to the entire child element
  // eslint-disable-next-line react/no-clone-element
  const childWithProps = React.cloneElement(children, getReferenceProps({ ref: childRef, ...children.props }));

  return (
    <>
      {childWithProps}
      {visible && wrappedTooltip}
    </>
  );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;
